import { useMemo } from 'react';

import { PART_REGISTRY } from 'containers/admin/clients/client/tabs-components/ai-assistant/components/chat/ai-chat-part-registry';
import type GuidedPromptPart from 'containers/admin/clients/client/tabs-components/ai-assistant/components/chat/parts/guided-prompt-part';
import type { GuidedPrompt } from 'containers/admin/clients/client/tabs-components/ai-assistant/helpers/guided-prompt-helpers';
import { PART_TYPES } from 'containers/admin/clients/client/tabs-components/ai-assistant/ai-assistant.constants';

export interface ChatMessagePart {
  type: string;
  data?: unknown;
  [key: string]: unknown;
}

export interface ChatMessage {
  id: string;
  isUser?: boolean;
  parts?: ChatMessagePart[];
  inputPartDismissed?: boolean;
  [key: string]: unknown;
}

export interface ActiveInputPart {
  message: ChatMessage;
  data: GuidedPrompt[];
  Component: typeof GuidedPromptPart;
}

/**
 * Checks whether the most recent AI message contains an input message part
 * and returns the resolved component and aggregated part data needed to render it.
 *
 * Only the most recent AI message can own the input area — any newer message
 * supersedes a prompt in an older one.
 *
 * @param {ChatMessage[]} messages - Chat messages.
 * @returns {ActiveInputPart | null} Active input part. `null` when no active input part exists.
 */
const useActiveInputPart = (messages: ChatMessage[]): ActiveInputPart | null =>
  useMemo(() => {
    const lastAiMessage = messages.findLast((message) => !message.isUser);

    if (!lastAiMessage || lastAiMessage.inputPartDismissed) return null;

    const parts = lastAiMessage.parts ?? [];
    const firstInputIndex = parts.findIndex(
      (part) => PART_REGISTRY[part.type]?.renderContext === 'input'
    );

    if (firstInputIndex < 0) return null;

    const firstInputPart = parts[firstInputIndex];
    const partEntry = PART_REGISTRY[firstInputPart.type];

    if (!partEntry) return null;

    const { component: Component } = partEntry;

    /**
     * Consecutive GUIDED_PROMPT parts are collapsed into a single `data` array
     * so the guided-prompt form can render them as a multi-step wizard.
     */
    if (firstInputPart.type === PART_TYPES.GUIDED_PROMPT) {
      const consecutiveGuidedPromptData: GuidedPrompt[] = [];

      for (let i = firstInputIndex; i < parts.length; i += 1) {
        if (parts[i].type !== PART_TYPES.GUIDED_PROMPT) break;

        consecutiveGuidedPromptData.push(parts[i].data as GuidedPrompt);
      }

      return {
        message: lastAiMessage,
        data: consecutiveGuidedPromptData,
        Component: Component as typeof GuidedPromptPart,
      };
    }

    return {
      message: lastAiMessage,
      data: firstInputPart.data as GuidedPrompt[],
      Component: Component as typeof GuidedPromptPart,
    };
  }, [messages]);

export default useActiveInputPart;
